/**
 * Shared caret/selection helpers for contenteditable elements.
 *
 * These utilities are DOM-only and safe to use in React components that manage
 * contenteditable fields. They help determine caret position relative to the
 * element and to programmatically place the caret at the start or end.
 */

/**
 * Returns true if the current window selection is collapsed and inside the given element.
 */
export function isSelectionCollapsedInside(el: HTMLElement): boolean {
  const sel = window.getSelection()
  if (!sel || !sel.anchorNode) return false
  return sel.isCollapsed && el.contains(sel.anchorNode)
}

/**
 * Returns true if the caret is at the start of the element's content.
 */
export function isAtStart(el: HTMLElement): boolean {
  const sel = window.getSelection()
  if (!sel || !isSelectionCollapsedInside(el)) return false
  const range = sel.getRangeAt(0)
  const testRange = document.createRange()
  testRange.selectNodeContents(el)
  testRange.setEnd(range.startContainer, range.startOffset)
  return testRange.toString().length === 0
}

/**
 * Returns true if the caret is at the end of the element's content.
 */
export function isAtEnd(el: HTMLElement): boolean {
  const sel = window.getSelection()
  if (!sel || !isSelectionCollapsedInside(el)) return false
  const range = sel.getRangeAt(0)
  const testRange = document.createRange()
  testRange.selectNodeContents(el)
  testRange.setStart(range.endContainer, range.endOffset)
  return testRange.toString().length === 0
}

/**
 * Places caret at the start or end of the element's content.
 */
export function placeCaret(
  el: HTMLElement,
  where: 'start' | 'end' = 'end',
): void {
  el.focus()
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(where === 'start')
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}
